/**
 * T550 (FR-UI-003) — Profile-Changelog hook.
 *
 * Reads `apps/web/src/data/changelog.json` + the user's last-seen
 * timestamp from localStorage. Returns the full entry list (DESC by
 * date), the unseen count, and a `markAsSeen` helper that persists
 * the current latest entry date back to localStorage.
 *
 * No server round-trip — the changelog ships as a static asset shipped
 * with each web build, so a fresh deploy automatically surfaces the
 * new entries to anyone whose localStorage cursor pre-dates them.
 */
import { useCallback, useMemo, useState } from 'react';
import rawEntries from '../data/changelog.json';

export type ChangelogEntryType = 'feature' | 'fix' | 'breaking';

export interface ChangelogEntry {
  date: string;
  title: string;
  description: string;
  type: ChangelogEntryType;
  ui_route?: string;
}

const STORAGE_KEY = 'changelog_last_seen';

function readLastSeen(): string {
  if (typeof window === 'undefined') return '1970-01-01';
  try {
    return window.localStorage.getItem(STORAGE_KEY) ?? '1970-01-01';
  } catch {
    return '1970-01-01';
  }
}

function writeLastSeen(value: string): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, value);
  } catch {
    // localStorage may be disabled (private window, quota) — degrade silently.
  }
}

// Exported for unit tests — the React hook below is a thin wrapper.
export function sortedDesc(entries: ChangelogEntry[]): ChangelogEntry[] {
  return [...entries].sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0));
}

export function countUnseen(entries: ChangelogEntry[], lastSeen: string): number {
  return entries.filter((e) => e.date > lastSeen).length;
}

export function latestDate(entries: ChangelogEntry[]): string | null {
  if (entries.length === 0) return null;
  return sortedDesc(entries)[0].date;
}

export const __testing = {
  STORAGE_KEY,
  readLastSeen,
  writeLastSeen,
};

export interface UseChangelogResult {
  entries: ChangelogEntry[];
  unseenCount: number;
  markAsSeen: () => void;
}

export function useChangelog(): UseChangelogResult {
  const entries = useMemo(
    () => sortedDesc(rawEntries as ChangelogEntry[]),
    []
  );
  const [lastSeen, setLastSeen] = useState<string>(() => readLastSeen());

  const unseenCount = useMemo(
    () => entries.filter((e) => e.date > lastSeen).length,
    [entries, lastSeen]
  );

  const markAsSeen = useCallback(() => {
    if (entries.length === 0) return;
    const latest = entries[0].date;
    writeLastSeen(latest);
    setLastSeen(latest);
  }, [entries]);

  return { entries, unseenCount, markAsSeen };
}
